import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Chip
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  PhotoCamera,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../services/api';

const ReceiptUpload = ({ shoppingListId, onUploadSuccess }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewDialog, setPreviewDialog] = useState({ open: false, receipt: null });

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    // Filter for image files only
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/') && 
      ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)
    );

    if (imageFiles.length === 0) {
      setError(t('receipts.invalidFileType', 'Please select PNG or JPG images only'));
      return;
    }

    if (imageFiles.length > 5) {
      setError(t('receipts.tooManyFiles', 'Maximum 5 files at once'));
      return;
    }

    imageFiles.forEach(uploadFile);
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    if (shoppingListId) {
      formData.append('shopping_list_id', shoppingListId.toString());
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await apiClient.post('/api/v1/receipts/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      setSuccess(t('receipts.uploadSuccess', 'Receipt uploaded successfully!'));
      
      if (onUploadSuccess) {
        onUploadSuccess(response);
      }

      // Refresh receipts list
      fetchReceipts();

    } catch (err) {
      setError(err.response?.data?.detail || t('receipts.uploadError', 'Upload failed'));
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress(0);
        setSuccess(null);
        setError(null);
      }, 3000);
    }
  };

  const fetchReceipts = async () => {
    try {
      const params = shoppingListId ? `?shopping_list_id=${shoppingListId}` : '';
      const data = await apiClient.get(`/api/v1/receipts${params}`);
      setReceipts(data);
    } catch (err) {
      console.error('Failed to fetch receipts:', err);
    }
  };

  const deleteReceipt = async (receiptId) => {
    try {
      await apiClient.delete(`/api/v1/receipts/${receiptId}`);
      setReceipts(receipts.filter(r => r.id !== receiptId));
    } catch (err) {
      setError(t('receipts.deleteError', 'Failed to delete receipt'));
    }
  };

  React.useEffect(() => {
    fetchReceipts();
  }, [shoppingListId]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {t('receipts.title', 'Receipts')}
      </Typography>

      {/* Upload Area */}
      <Paper
        sx={{
          p: 3,
          mb: 2,
          border: '2px dashed',
          borderColor: 'grey.300',
          backgroundColor: 'grey.50',
          cursor: 'pointer',
          '&:hover': { borderColor: 'primary.main', backgroundColor: 'grey.100' }
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDrop={handleDrop}
        onClick={() => document.getElementById('receipt-file-input').click()}
      >
        <Box textAlign="center">
          <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('receipts.dragDrop', 'Drag & drop receipt images here')}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('receipts.orClick', 'or click to select files')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('receipts.supportedFormats', 'Supports PNG, JPG (max 10MB each)')}
          </Typography>
        </Box>

        <input
          id="receipt-file-input"
          type="file"
          multiple
          accept="image/png,image/jpeg,image/jpg"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Paper>

      {/* Upload Progress */}
      {uploading && (
        <Box mb={2}>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary">
            {t('receipts.uploading', 'Uploading...')} {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Receipts Grid */}
      {receipts.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {t('receipts.uploaded', 'Uploaded Receipts')} ({receipts.length})
          </Typography>
          
          <Grid container spacing={2}>
            {receipts.map((receipt) => (
              <Grid item xs={12} sm={6} md={4} key={receipt.id}>
                <Card>
                  <Box position="relative">
                    <CardMedia
                      component="img"
                      height="200"
                      image={receipt.public_url}
                      alt={receipt.filename}
                      sx={{ cursor: 'pointer', objectFit: 'cover' }}
                      onClick={() => setPreviewDialog({ open: true, receipt })}
                    />
                    <IconButton
                      size="small"
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8, 
                        backgroundColor: 'rgba(255,255,255,0.8)' 
                      }}
                      onClick={() => deleteReceipt(receipt.id)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                  
                  <CardContent>
                    <Typography variant="body2" noWrap>
                      {receipt.filename}
                    </Typography>
                    <Box display="flex" gap={1} mt={1}>
                      {receipt.store_name && (
                        <Chip label={receipt.store_name} size="small" />
                      )}
                      {receipt.total_amount && (
                        <Chip label={`$${receipt.total_amount}`} size="small" color="primary" />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(receipt.created_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialog.open} 
        onClose={() => setPreviewDialog({ open: false, receipt: null })}
        maxWidth="md"
        fullWidth
      >
        {previewDialog.receipt && (
          <>
            <DialogTitle>{previewDialog.receipt.filename}</DialogTitle>
            <DialogContent>
              <img
                src={previewDialog.receipt.public_url}
                alt={previewDialog.receipt.filename}
                style={{ width: '100%', height: 'auto' }}
              />
              <Box mt={2}>
                {previewDialog.receipt.store_name && (
                  <Typography><strong>Store:</strong> {previewDialog.receipt.store_name}</Typography>
                )}
                {previewDialog.receipt.total_amount && (
                  <Typography><strong>Total:</strong> ${previewDialog.receipt.total_amount}</Typography>
                )}
                {previewDialog.receipt.purchase_date && (
                  <Typography><strong>Date:</strong> {previewDialog.receipt.purchase_date}</Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewDialog({ open: false, receipt: null })}>
                {t('common.buttons.close', 'Close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ReceiptUpload;